import { useMemo } from "react";
import type { ProposalContentOperation } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalPendingChangeCountsByCategory } from "work/proposals/negotiation/proposal-pending-change-counts";
import { type ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";

/**
 * The "not yet shared" count a proposal tab advertises: the actor's own unsent changes.
 *
 * Both pages carry the same six tabs, and both used to derive this inline — which is how they came to
 * pass a hard-coded zero for each of them after the legacy editor was deleted, leaving
 * `pending-change-badge` unable to render at all. One hook so the two pages cannot drift again.
 *
 * It used to answer for unsent COMMENTS per tab as well. A proposal has one conversation now rather
 * than a thread per field, so an unsent comment is not about any category and the same number on all
 * six tabs would only be repeating itself.
 *
 * Distinct from the unviewed badge beside it: that counts what SOMEBODY ELSE did and the viewer has not
 * looked at. This counts what the viewer themselves has staged and not yet sent.
 */
export type ProposalTabPendingCounts = {
  pendingChangeCount: (category: ProposalFieldCategoryType) => number;
};

export default function useProposalTabPendingCounts(
  /** Exactly what the next Save will persist; empty outside the canonical editor. */
  pendingOperations: readonly ProposalContentOperation[] | undefined,
): ProposalTabPendingCounts {
  const changeCounts = useMemo(
    () => proposalPendingChangeCountsByCategory(pendingOperations),
    [pendingOperations],
  );

  return {
    pendingChangeCount: (category) => changeCounts[category] ?? 0,
  };
}
